import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../api/axios";
import SidebarAdmin from "../../components/SidebarAdmin";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { formatLocalDateTime } from "../../utils/dateTime";

export default function ReportsPage() {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    status: "",
    source: "",
    search: "",
    limit: 500,
  });
  const [loading, setLoading] = useState(false);
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.status) params.status = filters.status;
      if (filters.source) params.source = filters.source;
      if (filters.limit) params.limit = filters.limit;

      const res = await axios.get("/admin/reports", { params });
      setLogs(res.data || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [filters.from, filters.limit, filters.source, filters.status, filters.to]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filteredLogs = useMemo(() => {
    const q = String(filters.search || "").trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((log) => {
      const hay = [
        log.to_number,
        log.user_email,
        log.user_name,
        log.content,
        log.status,
        log.source,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [filters.search, logs]);

  const exportExcel = async () => {
    const rows = filteredLogs.map((log) => ({
      Status: log.status,
      Route: String(log.source || "MANUAL").toUpperCase(),
      Type: log.message_type || "TEXT",
      To: log.to_number || "",
      User: log.user_name || "",
      Email: log.user_email || "",
      Message: log.content || "",
      Date: formatLocalDateTime(log.created_at),
    }));

    // Use secure ExcelJS instead of vulnerable xlsx
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Reports");
    
    // Add header row
    worksheet.columns = [
      { header: "Status", key: "Status", width: 15 },
      { header: "Route", key: "Route", width: 15 },
      { header: "Type", key: "Type", width: 10 },
      { header: "To", key: "To", width: 18 },
      { header: "User", key: "User", width: 20 },
      { header: "Email", key: "Email", width: 25 },
      { header: "Message", key: "Message", width: 40 },
      { header: "Date", key: "Date", width: 20 }
    ];
    
    // Add data rows
    rows.forEach(row => worksheet.addRow(row));
    
    // Generate buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "message_reports.xlsx";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Message Reports", 14, 16);

    const body = filteredLogs.map((log) => ([
      log.status || "",
      String(log.source || "MANUAL").toUpperCase(),
      log.message_type || "TEXT",
      log.to_number || "",
      log.user_email || "",
      (log.content || "").slice(0, 60),
      formatLocalDateTime(log.created_at),
    ]));

    autoTable(doc, {
      startY: 22,
      head: [["Status", "Route", "Type", "To", "User", "Message", "Date"]],
      body,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [31, 41, 55] },
      columnStyles: {
        5: { cellWidth: 70 },
        6: { cellWidth: 35 },
      },
    });

    doc.save("message_reports.pdf");
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gray-100">
      <SidebarAdmin />
      <div className="flex-1 p-3 md:p-6 lg:p-8 mt-14 lg:mt-0 overflow-y-auto">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 md:mb-6">
          <div>
            <h2 className="text-xl md:text-3xl font-bold">Message Reports</h2>
            <p className="text-sm text-gray-500">Track status, route (API/Manual), delivery & content</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportExcel}
              className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-green-700"
            >
              Export Excel
            </button>
            <button
              onClick={exportPdf}
              className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-red-700"
            >
              Export PDF
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div>
              <label className="text-xs text-gray-500">From</label>
              <div
                className="w-full border p-2 rounded-lg cursor-pointer focus-within:ring-2 focus-within:ring-blue-500"
                onClick={() => {
                  fromRef.current?.showPicker?.();
                  fromRef.current?.focus();
                }}
              >
                <input
                  ref={fromRef}
                  type="date"
                  className="w-full bg-transparent outline-none"
                  value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500">To</label>
              <div
                className="w-full border p-2 rounded-lg cursor-pointer focus-within:ring-2 focus-within:ring-blue-500"
                onClick={() => {
                  toRef.current?.showPicker?.();
                  toRef.current?.focus();
                }}
              >
                <input
                  ref={toRef}
                  type="date"
                  className="w-full bg-transparent outline-none"
                  value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500">Status</label>
              <select
                className="w-full border p-2 rounded-lg"
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="">All</option>
                <option value="SENT">SENT</option>
                <option value="DELIVERED">DELIVERED</option>
                <option value="READ">READ</option>
                <option value="FAILED">FAILED</option>
                <option value="PENDING">PENDING</option>
                <option value="PLAYED">PLAYED</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Route</label>
              <select
                className="w-full border p-2 rounded-lg"
                value={filters.source}
                onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
              >
                <option value="">All</option>
                <option value="MANUAL">MANUAL</option>
                <option value="API">API</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-500">Search</label>
              <input
                className="w-full border p-2 rounded-lg"
                placeholder="number, user, message..."
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-gray-500">
              Showing {filteredLogs.length} records
            </div>
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg font-bold hover:bg-gray-800 disabled:opacity-50"
            >
              {loading ? "Refreshing..." : "Apply Filters"}
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <table className="min-w-[900px] w-full text-left text-sm md:text-base">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th className="p-2 md:p-4 whitespace-nowrap">Status</th>
                <th className="p-2 md:p-4 whitespace-nowrap">Route</th>
                <th className="p-2 md:p-4 whitespace-nowrap">Type</th>
                <th className="p-2 md:p-4 whitespace-nowrap">To</th>
                <th className="p-2 md:p-4 whitespace-nowrap">User</th>
                <th className="p-2 md:p-4 whitespace-nowrap">Message</th>
                <th className="p-2 md:p-4 whitespace-nowrap">Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map(log => (
                <tr key={log.id} className="border-b hover:bg-gray-50">
                  <td className="p-2 md:p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      log.status === "SENT" || log.status === "DELIVERED" || log.status === "READ" || log.status === "PLAYED"
                        ? "bg-green-100 text-green-700"
                        : log.status === "FAILED"
                        ? "bg-red-100 text-red-700"
                        : "bg-gray-100 text-gray-600"
                    }`}>
                      {log.status}
                    </span>
                  </td>
                  <td className="p-2 md:p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      String(log.source || "MANUAL").toUpperCase() === "API"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-purple-100 text-purple-700"
                    }`}>
                      {String(log.source || "MANUAL").toUpperCase()}
                    </span>
                  </td>
                  <td className="p-2 md:p-4 whitespace-nowrap">{log.message_type || "TEXT"}</td>
                  <td className="p-2 md:p-4 whitespace-nowrap font-mono">{log.to_number || "—"}</td>
                  <td className="p-2 md:p-4 text-xs md:text-sm">
                    <div className="font-semibold text-gray-800">{log.user_name || "N/A"}</div>
                    <div className="text-gray-500">{log.user_email || ""}</div>
                  </td>
                  <td className="p-2 md:p-4 max-w-[280px] truncate" title={log.content || ""}>
                    {log.content || "—"}
                  </td>
                  <td className="p-2 md:p-4 whitespace-nowrap">
                    {formatLocalDateTime(log.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Note: Status shows the latest delivery state (Sent/Delivered/Read/Failed). If you need exact timestamps per state, we can add them to DB.
        </p>
      </div>
    </div>
  );
}